import * as React from "react";
import { connect } from "react-redux";
import get from "lodash/get";

import {
  LoyaltyProgressBar as LoyaltyProgressBarComponent,
  LoyaltyProgressBarProps,
} from "../../components/LoyaltyProgressBar/LoyaltyProgressBar";
import { getMemberTierCustomerAction } from "../../actions";
import { StoreState } from "../../reducers";
import {
  getIsMemberTierLoading,
  getMemberTierCustomer,
} from "../../reducers/methods";
import { customerAuthenticationOccurred } from "../../utils/customer";

/**
 * How long the component waits for the member tier record before treating it as
 * unretrievable and rendering nothing (REQ-LPB-041).
 *
 * TODO(ENH-376): unmeasured placeholder — set from real getMemberTierCustomer
 * latency in Phase 5/QA, see design.md D12. Deliberately not derived from the
 * two families of timeout already in this SDK: the 1000ms "Copied!" reverts are
 * transient UI feedback with no request behind them, and CouponCode's
 * 500ms x 30 polling budget is calibrated for waiting on an async server-side
 * job. `getMemberTierCustomer` is a plain GET and is neither.
 */
export const MEMBER_TIER_FETCH_TIMEOUT_MS = 3000;

interface LoyaltyProgressBarContainerProps extends LoyaltyProgressBarProps {
  getMemberTierCustomerAction: typeof getMemberTierCustomerAction;
  isLoading: ReturnType<typeof getIsMemberTierLoading>;
  memberTierCustomer: ReturnType<typeof getMemberTierCustomer>;
}

interface LoyaltyProgressBarContainerState {
  hasRequestTimedOut: boolean;
}

/**
 * ENH-376 — mirrors `containers/MemberTier`'s dispatch-on-mount against the
 * same `memberTier` slice (design.md D7). Reusing the slice is also what makes
 * two bars on one page agree by construction (REQ-LPB-061/062), whatever the
 * underlying fetch count.
 *
 * The component never writes: this container dispatches one GET and nothing
 * else, which is REQ-LPB-044/045/046 and REQ-LPS-029.
 */
export class LoyaltyProgressBar extends React.Component<
  LoyaltyProgressBarContainerProps,
  LoyaltyProgressBarContainerState
> {
  timeoutId: number | undefined;

  state: LoyaltyProgressBarContainerState = { hasRequestTimedOut: false };

  componentDidMount() {
    const isCustomerAuthenticated = get(
      this.props.data,
      "customer.authenticated",
      false
    );

    if (isCustomerAuthenticated) {
      if (!this.props.isLoading) {
        this.props.getMemberTierCustomerAction();
      }

      this.timeoutId = window.setTimeout(
        this.onFetchTimeout,
        MEMBER_TIER_FETCH_TIMEOUT_MS
      );
    }
  }

  componentDidUpdate(prevProps: LoyaltyProgressBarContainerProps) {
    // CodeRabbit, PR 1: every other member-tier-dispatching container
    // (RedemptionOptions, LedgerActivity, LedgerTotal, Coupons, MyRewards,
    // LedgerRewards, PointsExpiry, App.tsx) re-dispatches its fetch when
    // customerAuthenticationOccurred -- this container only ever dispatched
    // on mount. A visitor identified after mount (the ordinary post-login
    // case) left this bar stuck on its initial, pre-fetch record forever;
    // no other mounted consumer of this shared slice can be relied on to
    // trigger a fetch on this bar's behalf. Mirrors componentDidMount's own
    // dispatch-and-arm, with the same clear-before-arm discipline as the
    // isLoading re-arm branch below, since this can race the same shared
    // timer. The four sibling member-tier containers that still lack this
    // same fetch-on-authentication pattern (per the same review pass) share
    // this gap too, but fixing it here costs no parity with them -- this
    // container only affects its own data loading.
    if (customerAuthenticationOccurred(prevProps.data, this.props.data)) {
      if (!this.props.isLoading) {
        this.props.getMemberTierCustomerAction();
      }

      if (this.timeoutId !== undefined) {
        window.clearTimeout(this.timeoutId);
      }

      this.timeoutId = window.setTimeout(
        this.onFetchTimeout,
        MEMBER_TIER_FETCH_TIMEOUT_MS
      );
    }

    // Dwightbot round 1: hasRequestTimedOut was a one-way latch -- once set,
    // a record arriving moments later (isLoading -> false, real data in the
    // slice) still rendered the permanent "failure" state, because nothing
    // ever cleared it. The fetch is no longer in flight either way once
    // isLoading resolves, so the timeout flag has nothing left to signal;
    // let the resolver read the record that's actually there.
    if (prevProps.isLoading && !this.props.isLoading) {
      if (this.timeoutId !== undefined) {
        window.clearTimeout(this.timeoutId);
        this.timeoutId = undefined;
      }

      if (this.state.hasRequestTimedOut) {
        this.setState({ hasRequestTimedOut: false });
      }
    }

    // CodeRabbit, PR 1: isLoading is one shared slice-wide flag, set true by
    // any of this SDK's five member-tier-dispatching containers -- not just
    // this one. componentDidMount only arms the timeout for the very first
    // request; a later container's own dispatch can flip isLoading
    // false->true again on an already-mounted bar with no timer left to
    // catch it timing out. Re-arm on every false->true transition, the same
    // way componentDidMount arms the first one. This is the third instance
    // of the one-way-transition gap design.md flagged for this exact
    // component family after `hasRequestTimedOut` (2597c93) and
    // `markerImageFailed` (4478ca6) -- both were a boolean stuck true with no
    // path back; this one is the mirror shape, a timer not re-armed on the
    // way back to true. componentDidMount always arms one on the ordinary
    // first-load path (mount while isLoading is still false, dispatch, then
    // the dispatch's own reducer flips isLoading true) -- clear whatever's
    // already pending first, the same way the true->false branch above
    // does, or that mount-armed timer is orphaned: not cancelled by this
    // branch, not reachable by the true->false clear above (which only
    // fires on the *next* isLoading transition), not reachable by
    // componentWillUnmount either once this.timeoutId has been overwritten.
    // It still fires on its own schedule -- on a dead component, or, worse,
    // partway through whatever request is in flight when it goes off,
    // setting hasRequestTimedOut after a fraction of that request's real
    // budget. Exactly the premature timeout already ruled out once.
    if (!prevProps.isLoading && this.props.isLoading) {
      if (this.timeoutId !== undefined) {
        window.clearTimeout(this.timeoutId);
      }

      this.timeoutId = window.setTimeout(
        this.onFetchTimeout,
        MEMBER_TIER_FETCH_TIMEOUT_MS
      );
    }
  }

  componentWillUnmount() {
    if (this.timeoutId !== undefined) {
      window.clearTimeout(this.timeoutId);
      this.timeoutId = undefined;
    }
  }

  onFetchTimeout = () => {
    this.timeoutId = undefined;

    // Still in flight when the budget ran out. A record that arrived, or a
    // request that already failed, both leave `isLoading` false and are handled
    // from the slice itself.
    if (this.props.isLoading) {
      this.setState({ hasRequestTimedOut: true });
    }
  };

  render() {
    const { memberTierCustomer } = this.props;

    // Read straight off the raw customer record. The shared reducer does not
    // map this field and is deliberately left alone (design.md D7/D13) —
    // `TierUpgradeRequirement` reads the same field the same way today. It is
    // what separates a next tier blocked by a manual tier assignment from one
    // unreachable by earning, where the top-level `nextTier` object the reducer
    // already exposes cannot tell the two apart.
    //
    // Known platform limitation this reads through, not introduced here: the
    // field can be wrong for an `fbt-membertier` rule-ordering defect that no
    // client-side signal can detect or correct (design.md D14 and
    // finding-membertier-rule-order-bug.md). Fixing it upstream needs no change
    // here.
    const unavailableReason = get(
      memberTierCustomer,
      "variables.nextTier.unavailableReason",
      ""
    );

    return (
      <LoyaltyProgressBarComponent
        {...this.props}
        unavailableReason={unavailableReason}
        hasRequestTimedOut={this.state.hasRequestTimedOut}
      />
    );
  }
}

const mapStateToProp = (state: StoreState) => ({
  isLoading: getIsMemberTierLoading(state),
  memberTierCustomer: getMemberTierCustomer(state),
});

export default connect(mapStateToProp, {
  getMemberTierCustomerAction,
})(LoyaltyProgressBar);
